// /verify-email?token=... — user lands here from verification email
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import Logo from "../components/Logo";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // 'checking' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    if (!token) { setStatus("error"); setErrorMsg("Missing verification token. Open the link from your email."); return; }
    axios.post(`${API}/auth/verify-email`, { token })
      .then(() => { setStatus("success"); })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setStatus("error");
        setErrorMsg(typeof detail === "string" ? detail : "Verification failed");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/" data-testid="verify-home-link"><Logo size={28} /></Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white border border-zinc-200 rounded p-8 shadow-sm text-center" data-testid="verify-card">
            {status === "checking" && (
              <div data-testid="verify-loading-state">
                <Loader2 className="w-12 h-12 animate-spin text-[#002FA7] mx-auto mb-3" />
                <h1 className="text-lg font-semibold text-zinc-900">Verifying your email…</h1>
              </div>
            )}
            {status === "success" && (
              <div data-testid="verify-success-state">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h1 className="text-xl font-bold text-zinc-900 mb-2">Email verified</h1>
                <p className="text-sm text-zinc-500 mb-4">Your email address has been confirmed. Welcome to roster.ai.</p>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded"
                  data-testid="verify-go-dashboard-btn"
                >
                  Go to dashboard
                </button>
              </div>
            )}
            {status === "error" && (
              <div data-testid="verify-error-state">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <h1 className="text-xl font-bold text-zinc-900 mb-2">Couldn't verify</h1>
                <p className="text-sm text-zinc-500 mb-4">{errorMsg}</p>
                <p className="text-xs text-zinc-400 mb-4">
                  If your link has expired, sign in and click "Resend verification" from the banner at the top of the dashboard.
                </p>
                <Link to="/login" className="inline-block text-sm text-[#002FA7] hover:underline font-medium" data-testid="verify-back-to-login">
                  Back to sign in
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyEmail;
